import React, { useState } from "react";
import { PhoneCall, CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { trackEvent } from "./Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Terugbelverzoek in 2 velden (naam + telefoon). Optioneel bedrijfsnaam voor zakelijke pagina.
const CallbackForm = ({ source = "website", showCompany = false, buttonText = "Bel mij terug" }) => {
  const [form, setForm] = useState({ name: "", phone: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/callback`, {
        name: form.name,
        phone: form.phone,
        company: showCompany ? form.company || null : null,
        source,
        page: window.location.pathname,
      });
      setDone(true);
      trackEvent("callback_request", { source });
      toast.success("Bedankt! Wij bellen u zo snel mogelijk terug.");
    } catch (err) {
      toast.error("Er ging iets mis. Bel ons gerust direct op 06 - 83 65 35 11.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center text-center py-8" data-testid="callback-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-1">Terugbelverzoek ontvangen</h3>
        <p className="text-slate-600">Wij bellen u op werkdagen binnen één werkdag terug.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="callback-form">
      <div className="space-y-2">
        <Label htmlFor={`cb-name-${source}`} className="text-slate-700 font-medium">Naam *</Label>
        <Input
          id={`cb-name-${source}`}
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          minLength={2}
          placeholder="Uw naam"
          data-testid="callback-name-input"
          className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`cb-phone-${source}`} className="text-slate-700 font-medium">Telefoonnummer *</Label>
        <Input
          id={`cb-phone-${source}`}
          name="phone"
          type="tel"
          value={form.phone}
          onChange={handleChange}
          required
          minLength={8}
          placeholder="06 - 12 34 56 78"
          data-testid="callback-phone-input"
          className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
        />
      </div>
      {showCompany && (
        <div className="space-y-2">
          <Label htmlFor={`cb-company-${source}`} className="text-slate-700 font-medium">Bedrijf / VvE</Label>
          <Input
            id={`cb-company-${source}`}
            name="company"
            value={form.company}
            onChange={handleChange}
            placeholder="Naam van uw bedrijf of VvE"
            data-testid="callback-company-input"
            className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
          />
        </div>
      )}
      <Button
        type="submit"
        disabled={submitting}
        data-testid="callback-submit-btn"
        className="w-full h-13 py-4 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white text-base font-semibold rounded-xl shadow-lg shadow-sky-500/25 transition-all"
      >
        {submitting ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Versturen...</>
        ) : (
          <><PhoneCall className="w-5 h-5 mr-2" />{buttonText}</>
        )}
      </Button>
      <p className="text-xs text-slate-500 text-center">
        Wij bellen op werkdagen tussen 08:00 en 18:00. Geen spam, geen verplichtingen.
      </p>
    </form>
  );
};

export default CallbackForm;
